<script setup>
import Icon from '@/Components/UI/Icon.vue'
import SkeletonRow from '@/Components/UI/SkeletonRow.vue'

const props = defineProps({
  columns: { type: Array, required: true }, // [{ key, label, align, numeric, sortable, width }]
  rows: { type: Array, default: () => [] },
  rowKey: { type: [String, Function], default: 'id' },
  sortBy: { type: String, default: '' },
  sortDir: { type: String, default: 'asc' },
  overdueKey: { type: Function, default: null }, // (row) => boolean
  loading: { type: Boolean, default: false },
  rowHref: { type: Function, default: null }, // (row) => string | null
  skeletonRows: { type: Number, default: 8 },
  compact: { type: Boolean, default: false },
  responsiveCards: { type: Boolean, default: false },
  forceCards: { type: Boolean, default: false },
})
defineEmits(['sort', 'rowClick'])

const HIDE_CLASS = {
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
}
const colHideClass = (col) => col.hideBelow ? (HIDE_CLASS[col.hideBelow] || '') : ''

const getKey = (row) => (typeof props.rowKey === 'function' ? props.rowKey(row) : row[props.rowKey])
const cellPad = 'py-3 px-4'
const alignClass = (col) => col.numeric || col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
</script>

<template>
  <div
    v-if="!forceCards"
    :class="[
      'ui-table relative rounded-[var(--radius-table)] border border-[color:var(--line)] bg-[color:var(--surface)]',
      responsiveCards ? 'hidden md:block' : '',
      compact ? 'is-compact' : '',
    ]"
  >
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead class="sticky top-0 z-[var(--z-sticky)] bg-[color:var(--surfaceAlt)] text-[color:var(--inkSoft)]">
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :style="col.width ? `width:${col.width}` : ''"
              :class="[cellPad, alignClass(col), colHideClass(col), 'font-medium text-xs uppercase tracking-wide border-b border-[color:var(--line)]']"
            >
              <button
                v-if="col.sortable"
                type="button"
                class="inline-flex items-center gap-1 hover:text-[color:var(--ink)] transition-colors"
                @click="$emit('sort', col.key)"
              >
                {{ col.label }}
                <Icon
                  v-if="sortBy === col.key"
                  :name="sortDir === 'asc' ? 'arrow-up' : 'arrow-down'"
                  :size="12"
                />
                <Icon
                  v-else
                  name="sort"
                  :size="12"
                  class="opacity-40"
                />
              </button>
              <span v-else>{{ col.label }}</span>
            </th>
          </tr>
        </thead>

        <tbody v-if="loading">
          <SkeletonRow
            v-for="i in skeletonRows"
            :key="i"
            :cols="columns.length"
          />
        </tbody>

        <tbody v-else-if="rows.length === 0">
          <tr>
            <td
              :colspan="columns.length"
              class="p-0"
            >
              <slot name="empty">
                <div class="p-8 text-center text-[color:var(--muted)] text-sm">
                  No records.
                </div>
              </slot>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr
            v-for="row in rows"
            :key="getKey(row)"
            :class="[
              'group border-b border-[color:var(--line)] last:border-b-0 transition-colors',
              overdueKey && overdueKey(row) ? 'border-l-[3px] border-l-[color:var(--danger)]' : '',
              rowHref && rowHref(row) ? 'cursor-pointer hover:bg-[color:var(--surfaceAlt)]' : '',
            ]"
            @click="rowHref && rowHref(row) && $emit('rowClick', row)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              :class="[cellPad, alignClass(col), colHideClass(col), col.numeric && 'tabular-nums']"
            >
              <slot
                :name="`cell-${col.key}`"
                :row="row"
                :value="row[col.key]"
              >
                {{ row[col.key] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div
    v-if="responsiveCards || forceCards"
    :class="[forceCards ? 'block' : 'md:hidden', 'grid gap-2.5']"
  >
    <template v-if="loading">
      <div
        v-for="i in skeletonRows"
        :key="i"
        class="h-20 rounded-[var(--radius-button)] bg-[color:var(--surfaceAlt)] animate-pulse"
      />
    </template>
    <template v-else-if="rows.length === 0">
      <slot name="empty">
        <div class="p-8 text-center text-[color:var(--muted)] text-sm">
          No records.
        </div>
      </slot>
    </template>
    <template v-else>
      <component
        :is="rowHref && rowHref(row) ? 'a' : 'div'"
        v-for="row in rows"
        :key="getKey(row)"
        :href="rowHref && rowHref(row)"
        :class="[
          'block rounded-[var(--radius-button)] border border-[color:var(--line)] bg-[color:var(--surface)] p-3 transition-colors',
          rowHref && rowHref(row) ? 'cursor-pointer hover:bg-[color:var(--surfaceAlt)]' : '',
          overdueKey && overdueKey(row) ? 'border-l-[3px] border-l-[color:var(--danger)]' : '',
        ]"
        @click="rowHref && rowHref(row) && $emit('rowClick', row)"
      >
        <slot
          name="card"
          :row="row"
          :href="rowHref && rowHref(row)"
        >
          <div class="space-y-1.5">
            <div
              v-for="col in columns.slice(0, 3)"
              :key="col.key"
              class="flex items-baseline justify-between gap-3 text-sm"
            >
              <span class="text-xs text-[color:var(--muted)] uppercase tracking-wide">{{ col.label }}</span>
              <span class="min-w-0 truncate text-right">
                <slot
                  :name="`cell-${col.key}`"
                  :row="row"
                  :value="row[col.key]"
                >{{ row[col.key] }}</slot>
              </span>
            </div>
          </div>
        </slot>
      </component>
    </template>
  </div>
</template>
